import Loading from "@components/layout/Loading"
import Celebration from "@pages/verify/Celebration"
import { auth, functions } from "@utils/firebase"
import { useCurrentUserMutation, useGetUser, useGetUserBookings } from "@utils/hooks"
import type React from "react"
import { useState, useCallback } from "react"
import { InlineWidget, useCalendlyEventListener } from "react-calendly"
import { useAuthState } from "react-firebase-hooks/auth"
import { useHttpsCallable } from "react-firebase-hooks/functions"
import { useMutation, useQueryClient } from "react-query"
import { useLocation } from "react-router-dom"

const ScheduleCall: React.FC = () => {
  const { data: userData } = useGetUser()
  const { data: bookingData } = useGetUserBookings()
  const [user] = useAuthState(auth)
  const location = useLocation()
  const queryClient = useQueryClient()
  const { mutate } = useCurrentUserMutation()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [saveCalendlyBooking] = useHttpsCallable(functions, "user-on_calendly_booking")

  const mutationBookings = useMutation({
    mutationFn: async (uri_id: string) => {
      try {
        console.log("Attempting to save Calendly booking with URI:", uri_id)

        if (!uri_id) {
          throw new Error("Event UID is required")
        }

        const result = await saveCalendlyBooking({ event_uid: uri_id })
        console.log("Calendly booking result:", result)

        return result
      } catch (error) {
        console.error("Error in mutationBookings:", error)
        throw error
      }
    },
    onSuccess: async (data) => {
      console.log("Booking successful:", data)

      if (location.state?.pensionApplies) {
        mutate({
          process_pension_applied: location.state?.pensionApplies,
        })
      }

      // Invalidate both users and bookings queries to refresh data
      queryClient.invalidateQueries("users")
      queryClient.invalidateQueries("userscalendly")
      setLoading(false)
    },
    onError: (error: any) => {
      console.error("Booking failed:", error)
      setError(error?.message || "Failed to book the call. Please try again.")
      setLoading(false)
    },
  })

  const handleEventScheduled = useCallback(
    async (e: any) => {
      try {
        setLoading(true)
        setError(null)

        console.log("Calendly event scheduled:", e)

        if (!e?.data?.payload?.event?.uri) {
          throw new Error("Invalid event data received from Calendly")
        }

        const uri_id = e.data.payload.event.uri.replace("https://api.calendly.com/scheduled_events/", "")

        if (!uri_id) {
          throw new Error("Could not extract event ID from Calendly URI")
        }

        console.log("Extracted URI ID:", uri_id)

        await mutationBookings.mutateAsync(uri_id)
      } catch (error) {
        console.error("Error handling scheduled event:", error)
        setError(error instanceof Error ? error.message : "An unexpected error occurred")
        setLoading(false)
      }
    },
    [mutationBookings],
  )

  useCalendlyEventListener({
    onEventScheduled: handleEventScheduled,
  })

  if (loading || mutationBookings.isLoading) {
    return (
      <Loading
        messages={{
          title: "Booking your call...",
          subtitle: "Please wait while we confirm your appointment",
        }}
      />
    )
  }

  if (error) {
    return (
      <div className="w-full grow flex items-center justify-center">
        <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">⚠️</div>
            <h2 className="text-lg font-semibold mb-2">Booking Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null)
                setLoading(false)
              }}
              className="btn-primary-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Check if user has a successful booking by checking both userData.calendly_event_uid and bookingData
  const hasSuccessfulBooking = userData?.calendly_event_uid && bookingData

  return (
    <>
      {!hasSuccessfulBooking ? (
        <InlineWidget
          prefill={{
            email: userData?.email_address || user?.email || "",
            name:
              userData?.first_name && userData?.last_name
                ? `${userData.first_name} ${userData.last_name}`
                : user?.displayName || "",
          }}
          styles={{
            width: "100%",
            height: 870,
            overflow: "hidden",
          }}
          pageSettings={{
            backgroundColor: "F8F7F3",
            hideGdprBanner: true,
            hideEventTypeDetails: true,
            hideLandingPageDetails: false,
            primaryColor: "333333",
            textColor: "333333",
          }}
          url="https://calendly.com/thisispenny/30min"
        />
      ) : (
        <Celebration />
      )}
    </>
  )
}

export default ScheduleCall
